import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Reasons the camera can be unavailable. The UI maps these to pt-BR copy.
 */
export const MediaError = {
  UNSUPPORTED: "unsupported",
  INSECURE: "insecure",
  DENIED: "denied",
  NOT_FOUND: "notFound",
  IN_USE: "inUse",
  UNKNOWN: "unknown",
};

/**
 * Constraints are tried in order until one resolves.
 *
 * In-app wallet browsers (Phantom, Solflare) tunnel getUserMedia through the
 * host WebView and frequently expose a single unlabelled device, so a request
 * that names facingMode can fail with OverconstrainedError where a bare
 * request succeeds. Asking for the ideal resolution first keeps a decent frame
 * on browsers that can honour it.
 */
const CONSTRAINT_LADDER = [
  {
    audio: false,
    video: {
      facingMode: { ideal: "environment" },
      width: { ideal: 1280 },
      height: { ideal: 720 },
    },
  },
  { audio: false, video: { facingMode: "environment" } },
  { audio: false, video: true },
];

function classify(err) {
  switch (err && err.name) {
    case "NotAllowedError":
    case "PermissionDeniedError":
    case "SecurityError":
      return MediaError.DENIED;
    case "NotFoundError":
    case "DevicesNotFoundError":
    case "OverconstrainedError":
      return MediaError.NOT_FOUND;
    case "NotReadableError":
    case "TrackStartError":
    case "AbortError":
      return MediaError.IN_USE;
    default:
      return MediaError.UNKNOWN;
  }
}

function stopStream(stream) {
  if (!stream) return;
  stream.getTracks().forEach((track) => track.stop());
}

/**
 * Requests a camera stream, reporting why it failed instead of resolving to a
 * silent null. The request is explicit so the retry can run inside a user
 * activation window, which is what unblocks WebViews that refuse a permission
 * prompt raised from page load alone.
 */
export function useUserMedia() {
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);
  const [isRequesting, setIsRequesting] = useState(false);

  const streamRef = useRef(null);
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopStream(streamRef.current);
      streamRef.current = null;
    };
  }, []);

  const request = useCallback(async () => {
    if (streamRef.current || inFlightRef.current) return;

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      // A missing mediaDevices means either a non-secure origin or a WebView
      // built without capture support. The two need different instructions.
      setError(
        window.isSecureContext === false
          ? MediaError.INSECURE
          : MediaError.UNSUPPORTED
      );
      return;
    }

    inFlightRef.current = true;
    setIsRequesting(true);
    setError(null);

    let lastError = null;
    for (const constraints of CONSTRAINT_LADDER) {
      try {
        const granted = await navigator.mediaDevices.getUserMedia(constraints);
        if (!mountedRef.current) {
          stopStream(granted);
          inFlightRef.current = false;
          return;
        }
        streamRef.current = granted;
        setStream(granted);
        setIsRequesting(false);
        inFlightRef.current = false;
        return;
      } catch (err) {
        lastError = err;
        // A denial is a decision, not a constraint problem: stop climbing the
        // ladder so the user is not prompted three times in a row.
        if (classify(err) === MediaError.DENIED) break;
      }
    }

    inFlightRef.current = false;
    if (!mountedRef.current) return;
    setIsRequesting(false);
    setError(classify(lastError));
  }, []);

  return { stream, error, isRequesting, request };
}
